import React, { FC, useMemo, useState } from 'react';
import { ReactComponent as CancelTripIcon } from 'assets/icons/cancel-trip-modal-icon.svg';
import { toast } from 'react-toastify';
import { Button, Input, Textarea, ToastMessage, BaseModal } from '@cokitchen/cokitchen-components';
import { OrderType } from 'models/order.model';
import { apiService } from 'utils/api';
import { EditAltIcon } from 'cokitchen-icons';

const CANCEL_TRIP_REASONS = [
  {
    internal: 'Wrong delivery address (reordered)',
    external: 'Order cancelled due to incorrect address. A new order has been placed.'
  },
  {
    internal: 'Wrong delivery address',
    external: 'Order cancelled due to incorrect delivery address.'
  },
  {
    internal: 'Wanted to change order details (reordered)',
    external: 'Order cancelled to allow an update. A new order has been placed.'
  },
  { internal: 'Forgot to use discount', external: 'Order cancelled at customer’s request.' },
  {
    internal: 'Forgot to use discount (reordered)',
    external: 'Order cancelled and reordered to apply discount.'
  },
  {
    internal: 'Just testing the app',
    external: 'Order cancelled as it was made for testing purposes.'
  },
  { internal: 'Order taking too long', external: 'Order cancelled due to delivery delay.' },
  { internal: 'No longer need order', external: 'Order cancelled at customer’s request.' },
  { internal: 'Ordered by mistake', external: 'Order cancelled as it was placed by mistake.' },
  { internal: 'Payment error/ issues', external: 'Order cancelled due to payment issues.' },
  { internal: 'Delivery fee too high', external: 'Order cancelled due to delivery fee concerns.' },
  { internal: 'Didn’t get confirmation', external: 'Order cancelled due to confirmation issues.' },
  { internal: 'App glitch / duplicate order', external: 'Order cancelled due to an app error.' },
  { internal: 'Other', external: '' }
];

interface CancelTripModalProps {
  show: boolean;
  onClose: () => void;
  order: OrderType;
}

interface CancelTripFooterProps {
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  disabled: boolean;
}

const CancelTripFooter: FC<CancelTripFooterProps> = ({
  onCancel,
  onConfirm,
  loading,
  disabled
}) => (
  <div className='grid grid-cols-2 gap-x-4'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Cancel
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      loading={loading}
      disabled={disabled}
      onClick={onConfirm}>
      Confirm
    </Button>
  </div>
);

const CancelTripModal: FC<CancelTripModalProps> = ({ show, onClose, order }) => {
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');
  const [comment, setComment] = useState('');

  const [showCustomMessage, setShowCustomMessage] = useState(false);
  const [customMessage, setCustomMessage] = useState('');

  const cancellationNote = useMemo(() => {
    const cancelled_customer_note = showCustomMessage
      ? customMessage
      : CANCEL_TRIP_REASONS.find((r) => r.internal === value)?.external ?? '';

    if (value === 'Other') {
      return { cancelled_note: comment, cancelled_customer_note };
    }

    return { cancelled_note: value, cancelled_customer_note };
  }, [value, comment, showCustomMessage, customMessage]);

  const noteIsEmpty = useMemo(() => {
    return !cancellationNote.cancelled_customer_note || !cancellationNote.cancelled_note;
  }, [cancellationNote]);

  const handleChange = (reason: { internal: string; external: string }) => {
    setValue(reason.internal);

    if (reason.internal === 'Other') {
      setShowCustomMessage(true);
    } else {
      setCustomMessage('');
      setShowCustomMessage(false);
    }
  };

  const handleEditCustomerNote = () => {
    setShowCustomMessage(true);
    setCustomMessage(cancellationNote.cancelled_customer_note);
  };

  const cancelTrip = async () => {
    try {
      setLoading(true);
      await apiService.post('logistics/order/cancel', {
        data: {
          order_code: order.order_code,
          ...cancellationNote
        },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      toast(<ToastMessage type='success' message='Trip cancelled successfully' />);
      onClose();
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={show}
      position='right'
      title='Cancel trip'
      size='wide'
      className='w-[70vw] max-w-[unset]'
      description={`Select reason why you are cancelling this trip #${
        order?.order_code?.toUpperCase() ?? ''
      }`}
      titleIcon={<CancelTripIcon />}
      titleIconColor='danger'
      onRequestClose={onClose}
      footer={
        <CancelTripFooter
          loading={loading}
          onCancel={onClose}
          onConfirm={cancelTrip}
          disabled={noteIsEmpty}
        />
      }>
      <div className='grid gap-6 grid-cols-2 mt-2'>
        {CANCEL_TRIP_REASONS.map((reason, i) => (
          <Input
            key={i}
            value={value}
            label={reason.internal}
            type='radio'
            onChange={() => handleChange(reason)}
            name='cancel-trip'
          />
        ))}
      </div>
      {value === 'Other' && (
        <div className='mt-6'>
          <Textarea
            label='Enter reason'
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder='Add a comment...'
            className='w-full'
          />
        </div>
      )}
      {(cancellationNote.cancelled_customer_note || showCustomMessage) && (
        <section className='mt-7'>
          <div className='flex justify-between gap-3'>
            <strong className='text-sm'>Message to be sent to user</strong>
            {!showCustomMessage && (
              <Button
                color='primary'
                variant='link'
                className='text-sm underline'
                onClick={handleEditCustomerNote}>
                <EditAltIcon />
                Edit
              </Button>
            )}
          </div>
          {value === 'Other' || showCustomMessage ? (
            <Textarea
              autoFocus
              placeholder='Enter notification to be sent to user'
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              className='w-full'
            />
          ) : (
            <p className='bg-green-1 border-green-5 rounded-xl p-4 mt-2'>
              {cancellationNote.cancelled_customer_note}
            </p>
          )}
        </section>
      )}
    </BaseModal>
  );
};

export default CancelTripModal;
